import { EmailResendIcon, PenIcon, TrashIcon } from "@xola/icons";
import { Avatar, Badge, flash, Logo, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import React from "react";
import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import { Delegate, Guide, Permission } from "types";
import { IsGranted } from "../../Common/IsGranted";
import { NoTranslate } from "../../Common/NoTranslate";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { api } from "@/api/api";
import { xolaUri } from "@/api/xolaUri";
import { useSeller } from "@/hooks/useAccount";
import { useResource } from "@/hooks/useResource";

interface GuideItemProps {
    guide: Guide;
    onDelete: (id: string) => void;
}

export const GuideItem = ({ guide, onDelete }: GuideItemProps) => {
    const history = useHistory();
    const seller = useSeller();
    const { data: delegate } = useResource<Delegate>(["users", seller.id, "delegates", guide.user.id], null, {
        apiConfig: {
            suppressErrorToast: [404],
        },
    });

    const { mutateAsync: resendInvite, isLoading: isResendLoading } = useMutation(async (userId: ID) => {
        return api.get(`/users/${userId}/resendConfirmation`);
    });

    const handleResendInvite = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        if (isResendLoading) {
            return;
        }

        try {
            await resendInvite(guide.user.id);
            flash.success(`Email confirmation re-sent to ${delegate?.user?.email}`);
        } catch {
            flash.error("Error resending confirmation email. Please try again.");
        }
    };

    const handleGuideEdit = () => {
        history.push(`/resources/guides/${guide.id}/edit`);
    };

    const handleDeleteGuide = (e: Event) => {
        e.stopPropagation();
        onDelete(guide.id);
    };

    const pictureSuffix = String(guide.updatedAt?.unix() ?? guide.id);
    const pictureSource = guide?.picture?.id
        ? `${xolaUri}/api/sellers/${seller.id}/guides/${guide.id}/picture?size=medium&cache_key=${pictureSuffix}`
        : undefined;

    return (
        <div
            className={clsx(
                "ui-guide-item flex cursor-pointer items-center overflow-hidden border-b border-b-gray-light px-5 py-4.5 hover:bg-gray-lighter xl:px-10",
            )}
            onClick={handleGuideEdit}
        >
            {pictureSource ? (
                <Logo src={pictureSource} className="mr-3 h-12 w-12 rounded-full" />
            ) : (
                <Avatar color="bg-primary-lighter" name={guide.name} size="medium" className="no-translate mr-3" />
            )}

            <div className="w-2/6 flex-shrink-0">
                <NoTranslate as="h3" className="truncate text-lg font-bold leading-p2 tracking-tightest">
                    {guide.name}
                </NoTranslate>
            </div>
            <div className="flex flex-auto">
                <Badge color={delegate?.user?.enabled ? "success" : "warning"}>
                    {delegate?.user?.enabled ? "Confirmed" : "Pending"}
                </Badge>
            </div>
            <div className="flex flex-auto justify-end space-x-2">
                {delegate && !delegate.user.enabled && (
                    <Tooltip content="Resend Confirmation Email">
                        <SecondaryButton
                            data-testid="guide-item-resend-invite-button"
                            size="small"
                            disabled={isResendLoading}
                            onClick={handleResendInvite}
                        >
                            <EmailResendIcon />
                        </SecondaryButton>
                    </Tooltip>
                )}
                <SecondaryButton data-testid="guide-item-edit-button" size="small" onClick={handleGuideEdit}>
                    <PenIcon />
                </SecondaryButton>
                <IsGranted permissions={Permission.RESOURCES_GUIDES_ALL_VIEW}>
                    <SecondaryButton data-testid="guide-item-remove-button" size="small" onClick={handleDeleteGuide}>
                        <TrashIcon />
                    </SecondaryButton>
                </IsGranted>
            </div>
        </div>
    );
};
